import { useEffect, useState, useRef } from 'react'
import { useSelector } from 'react-redux'
import { logger } from '../../../utils/dccLogger'
import useE3DAvailability from '../../../hooks/useE3DAvailability'
import config from '../../../config'
import { isAuthenticated } from '../../../services/e3DAuthService'
import { isGrpLoggedInSelector } from '../../../redux/selectors/e3DGrpAuthSelectors'
import languageSelector from '../../../redux/selectors/i18n/languageSelector'
import countrySelector from '../../../redux/selectors/i18n/countrySelector'
import brandSelector from '../../../redux/selectors/brandSelector'
import { useE3DReinit } from '../e3d-context/E3DReinitContext'
import useE3DTokenRefresh from './useE3DTokenRefresh'
import { mapCountryCodeToISO3 } from '../../../utils/countryCodeMapper'
import useE3DParamsInit from './useE3DParamsInit'
import e3dShouldInitIn3DModeSelector from '../../../redux/selectors/e3dShouldInitIn3DModeSelector'
import aveStringSelector from '../../../redux/selectors/aveStringSelector'
import configuredCarlineNameSelector from '../../../redux/selectors/carlines/configuredCarlineNameSelector'
import consumptionDataSelector from '../../../redux/selectors/consumptionDataSelector'
import { normalizeConsumptionData } from '../../miwi-feature-app-loader/MiwiFeatureAppLoader'

const getAuthTokens = (): { accessToken: string; idToken: string } | null => {
    try {
        let authData = sessionStorage.getItem('auth:token')
        if (!authData) {
            authData = localStorage.getItem('auth:token')
        }
        if (authData) {
            const parsedData = JSON.parse(authData)
            sessionStorage.setItem('auth:token', authData)
            return {
                accessToken: parsedData.accessToken,
                idToken: parsedData.idToken,
            }
        }
        return null
    } catch (error) {
        logger.error(`Error retrieving auth tokens: ${error}`)
        return null
    }
}

const mapBrandToE3D = (brand: string): string => {
    const brandMap = {
        audi: 'A',
        vw: 'V',
        seat: 'S',
        skoda: 'C',
        bentley: 'E',
        lamborghini: 'L',
        man: 'M',
        vwn: 'N',
        porsche: 'P',
        cupra: 'S',
    }
    return brandMap[brand.toLowerCase()] || 'A'
}

const ensureManualImageOverride = (): (() => void) => {
    let intervalId: ReturnType<typeof setInterval> | null = null
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const trySetManual = (): boolean => {
        if (window.E3D?.session) {
            try {
                window.E3D.session.setValues({ imageOverrideMode: 'manual' })
                logger.info('E3D: Set imageOverrideMode to manual on session')
                return true
            } catch (e) {
                logger.warn('E3D: Failed to set imageOverrideMode to manual', e)
                return true
            }
        }
        return false
    }

    if (!trySetManual()) {
        intervalId = setInterval(() => {
            if (trySetManual() && intervalId) {
                clearInterval(intervalId)
                intervalId = null
            }
        }, 100)
        timeoutId = setTimeout(() => {
            if (intervalId) {
                clearInterval(intervalId)
                intervalId = null
                logger.warn('E3D: Timeout waiting for session to set imageOverrideMode')
            }
        }, 5000)
    }

    return () => {
        if (intervalId) { clearInterval(intervalId) }
        if (timeoutId) { clearTimeout(timeoutId) }
    }
}

const useE3DInitialization = (is3DExhibitionEnabled: boolean): void => {
    const isE3DAvailable = useE3DAvailability(is3DExhibitionEnabled)
    const shouldStartIn3DMode = useSelector(e3dShouldInitIn3DModeSelector)
    const isGrpAuthenticated = useSelector(isGrpLoggedInSelector)
    const language = useSelector(languageSelector)
    const country = useSelector(countrySelector)
    const brand = useSelector(brandSelector)
    const { wasE3DSidUsed } = useE3DParamsInit()
    const [hasInitialized, setHasInitialized] = useState(false)
    const { triggerReinit } = useE3DReinit()
    const hasAttemptedResumeRef = useRef<string | null>(null)
    const aveString = useSelector(aveStringSelector)
    const carlineName = useSelector(configuredCarlineNameSelector)
    const consumptionData = useSelector(consumptionDataSelector)

    useE3DTokenRefresh(is3DExhibitionEnabled)

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search)
        let e3dSid = urlParams.get('e3d-sid')

        if (!e3dSid) {
            try {
                e3dSid = sessionStorage.getItem('e3d_sid')
            } catch { /* no-op */ }
        }
        const hasE3dSid = Boolean(e3dSid)

        if (!is3DExhibitionEnabled || !isE3DAvailable || !isGrpAuthenticated || hasInitialized) {
            return
        }

        if (!isAuthenticated()) {
            logger.warn('User is not authenticated or token has expired')
            return
        }

        const tokens = getAuthTokens()
        if (!tokens) {
            logger.error('No access or ID token available for E3D initialization')
            return
        }

        try {
            const brandCode = mapBrandToE3D(brand)
            const market = mapCountryCodeToISO3(country)
            const partnerToken = config.E3DEX_PARTNER_TOKEN

            if (!partnerToken) {
                logger.error('E3DEX_PARTNER_TOKEN is empty or undefined — E3D API calls will fail')
            }

            const initOptions: E3D.E3DInitOptions = {
                partnerToken,
                brand: brandCode,
                market,
                locale: language,
                getAccessTokenCallback: () => {
                    const freshTokens = getAuthTokens()
                    if (!freshTokens) {
                        logger.warn('getAccessTokenCallback: no tokens found in storage')
                        return null
                    }
                    return freshTokens.accessToken
                },
                getIdTokenCallback: () => {
                    const freshTokens = getAuthTokens()
                    if (!freshTokens) {
                        logger.warn('getIdTokenCallback: no tokens found in storage')
                        return null
                    }
                    return freshTokens.idToken
                },
            }

            logger.info(`Initializing E3D with brand: ${brandCode}, market: ${market}, locale: ${language}, partnerToken: ${partnerToken ? '[SET]' : '[EMPTY]'}`)

            if (window.E3D) {
                const sessionDefaults = shouldStartIn3DMode
                    ? { activeExperience: '3D', imageOverrideMode: 'manual' }
                    : { imageOverrideMode: 'manual' }

                if (!wasE3DSidUsed) {
                    window.E3D.init(initOptions, sessionDefaults)
                } else {
                    window.E3D.init(initOptions)
                }
                setHasInitialized(true)
                logger.info('E3D successfully initialized')

                const cleanupOverride = ensureManualImageOverride()
                window.addEventListener('beforeunload', cleanupOverride)

                if (hasE3dSid && e3dSid && hasAttemptedResumeRef.current !== e3dSid) {
                    logger.info('e3d-sid present after initialization, calling resumeSessionFromParams()')
                    hasAttemptedResumeRef.current = e3dSid

                    if (!urlParams.has('e3d-sid')) {
                        const currentUrl = new URL(window.location.href)
                        currentUrl.searchParams.set('e3d-sid', e3dSid)
                        const newUrl = currentUrl.pathname +
                            (currentUrl.searchParams.toString() ? `?${currentUrl.searchParams.toString()}` : '')
                        window.history.replaceState({}, document.title, newUrl)
                    }

                    try {
                        window.E3D.resumeSessionFromParams()
                        setTimeout(() => {
                            if (window.E3D?.showCar && aveString) {
                                const wltpText = normalizeConsumptionData(consumptionData).join(' ')
                                logger.info('Calling showCar after e3d-sid resume', { aveString })
                                window.E3D.showCar(aveString, carlineName, wltpText)
                            }
                        }, 500)
                    } catch (error) {
                        logger.error(`Error resuming E3D session after init: ${error}`)
                    }
                }

                triggerReinit()
            } else {
                logger.error('E3D global object not available')
            }
        } catch (error) {
            logger.error(`Error initializing E3D: ${error}`)
        }
    }, [is3DExhibitionEnabled, isE3DAvailable, isGrpAuthenticated, hasInitialized, language, country, brand, wasE3DSidUsed, triggerReinit, shouldStartIn3DMode, aveString, carlineName, consumptionData])
    // eslint-disable-next-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!is3DExhibitionEnabled || !isE3DAvailable || !isGrpAuthenticated || !hasInitialized) {
            return
        }

        if (!isAuthenticated()) {
            return
        }

        if (!window.E3D) {
            return
        }

        const urlParams = new URLSearchParams(window.location.search)
        let e3dSid = urlParams.get('e3d-sid')

        if (!e3dSid) {
            try {
                e3dSid = sessionStorage.getItem('e3d_sid')
            } catch { /* no-op */ }
        }

        if (e3dSid && hasAttemptedResumeRef.current !== e3dSid) {
            logger.info('e3d-sid present after authentication, calling resumeSessionFromParams()')
            hasAttemptedResumeRef.current = e3dSid

            if (!urlParams.has('e3d-sid')) {
                const currentUrl = new URL(window.location.href)
                currentUrl.searchParams.set('e3d-sid', e3dSid)
                const newUrl = currentUrl.pathname +
                    (currentUrl.searchParams.toString() ? `?${currentUrl.searchParams.toString()}` : '')
                window.history.replaceState({}, document.title, newUrl)
            }

            try {
                window.E3D.resumeSessionFromParams()

                setTimeout(() => {
                    if (window.E3D?.showCar && aveString) {
                        const wltpText = normalizeConsumptionData(consumptionData).join(' ')
                        logger.info('Calling showCar after e3d-sid auth resume', { aveString })
                        window.E3D.showCar(aveString, carlineName, wltpText)
                    }
                }, 500)
            } catch (error) {
                logger.error(`Error resuming E3D session from params: ${error}`)
            }
        }
    }, [is3DExhibitionEnabled, isE3DAvailable, isGrpAuthenticated, hasInitialized, aveString, carlineName, consumptionData])
}

export default useE3DInitialization
